import axios from "axios";

const TOKEN_KEY = "em_access_token";

// Resolve backend base URL:
// - In a browser served over http(s) (preview or production custom domain),
//   use the current origin so API calls are SAME-ORIGIN and never trip CORS.
//   The Emergent ingress routes /api/* on the frontend's domain to the backend.
// - In a Capacitor/native wrapper (capacitor:// or file://), fall back to the
//   configured REACT_APP_BACKEND_URL since there is no ingress to route on.
function resolveBackendBase() {
  try {
    if (typeof window !== "undefined" && window.location) {
      const proto = window.location.protocol;
      if (proto === "http:" || proto === "https:") return window.location.origin;
    }
  } catch {
    /* fall through */
  }
  return process.env.REACT_APP_BACKEND_URL || "";
}

const BACKEND_BASE = resolveBackendBase();

// Native (Capacitor) shells have no ingress cookies, so the token persists in
// app-sandboxed storage there. On the web we keep the token in memory only and
// rely on the httpOnly refresh cookie to restore sessions.
const IS_NATIVE_SHELL = (() => {
  try {
    const proto = window.location.protocol;
    return proto !== "http:" && proto !== "https:";
  } catch {
    return false;
  }
})();

let accessToken = IS_NATIVE_SHELL ? localStorage.getItem(TOKEN_KEY) : null;
if (!IS_NATIVE_SHELL) {
  try { localStorage.removeItem(TOKEN_KEY); } catch { /* noop */ }
}

export function setAccessToken(token) {
  accessToken = token;
  if (!IS_NATIVE_SHELL) return;
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
  } else {
    localStorage.removeItem(TOKEN_KEY);
  }
}

export function getAccessToken() {
  return accessToken;
}

export function clearAccessToken() {
  accessToken = null;
  try { localStorage.removeItem(TOKEN_KEY); } catch { /* noop */ }
}

const API = axios.create({
  baseURL: `${BACKEND_BASE}/api`,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// Attach token to every request
API.interceptors.request.use((config) => {
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

// On 401, try refresh then retry
API.interceptors.response.use(
  (res) => res,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url?.includes("/auth/login") && !originalRequest.url?.includes("/auth/register")) {
      originalRequest._retry = true;
      try {
        const { data } = await axios.post(
          `${BACKEND_BASE}/api/auth/refresh`,
          {},
          {
            withCredentials: true,
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
          }
        );
        if (data.access_token) {
          setAccessToken(data.access_token);
        }
        return API(originalRequest);
      } catch {
        clearAccessToken();
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

export { BACKEND_BASE };
export default API;
